import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircleIcon, XCircleIcon, LoaderIcon, HomeIcon, RefreshCwIcon, AlertCircleIcon, MailIcon } from 'lucide-react';
export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const {
    verifyEmail,
    currentUser,
    refreshUser,
    userType,
    sendVerificationEmail
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  // FIXED: Enhanced verification handling with better debugging
  useEffect(() => {
    const verifyUserEmail = async () => {
      // Get the action code (oobCode) from the URL
      const actionCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');
      const continueUrl = searchParams.get('continueUrl');
      const redirectRequested = searchParams.get('redirect') === 'true';
      console.log('VerifyEmailPage loaded with params:', {
        actionCode: actionCode ? '(exists)' : '(missing)',
        mode,
        continueUrl,
        redirectRequested
      });
      // If there's no action code and this is just a redirect back from email client
      if (!actionCode && redirectRequested) {
        try {
          if (currentUser) {
            console.log('Redirected back from email, refreshing user to check verification status');
            console.log('Current user email:', currentUser.email);
            console.log('Current verification status:', currentUser.emailVerified);
            await refreshUser();
            console.log('User refreshed, verification status:', currentUser.emailVerified);
            setSuccess(currentUser.emailVerified);
            if (!currentUser.emailVerified) {
              setError('Your email is not verified yet. Please check your inbox and click the verification link in the email.');
              setErrorCode('not-verified');
            }
          } else {
            console.log('No current user found');
            setError('You need to be logged in to verify your email.');
            setErrorCode('not-logged-in');
          }
        } catch (error: any) {
          console.error('Error refreshing user:', error);
          setError(`Failed to check verification status: ${error.message || 'Unknown error'}`);
          setErrorCode(error.code || 'unknown-error');
        } finally {
          setLoading(false);
        }
        return;
      }
      // If there's no action code or mode is not verifyEmail, show error
      if (!actionCode || mode !== 'verifyEmail') {
        console.error('Invalid verification link parameters:', {
          actionCode,
          mode
        });
        setError('Invalid verification link. Please try again or request a new verification email.');
        setErrorCode('invalid-link');
        setLoading(false);
        return;
      }
      try {
        setVerifying(true);
        console.log('Verifying email with action code:', actionCode.substring(0, 5) + '...');
        // Verify the email
        await verifyEmail(actionCode);
        console.log('Email verification successful');
        // Refresh user to get updated emailVerified status
        if (currentUser) {
          console.log('Refreshing user after verification');
          await refreshUser();
          console.log('User refreshed, emailVerified:', currentUser.emailVerified);
        }
        setSuccess(true);
        // Auto-redirect after successful verification
        setTimeout(() => {
          // If a continue URL is provided, use it
          if (continueUrl) {
            console.log('Redirecting to continue URL:', continueUrl);
            window.location.href = continueUrl;
          } else {
            // Otherwise redirect based on user type
            console.log('Redirecting based on user type:', userType);
            if (userType === 'teacher') {
              navigate('/teacher-dashboard');
            } else if (userType === 'student') {
              navigate('/student-dashboard');
            } else {
              navigate('/');
            }
          }
        }, 3000);
      } catch (error: any) {
        console.error('Error verifying email:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        // Provide more specific error messages based on error code
        if (error.code === 'auth/invalid-action-code') {
          setError('The verification link has expired or has already been used. Please request a new verification email.');
          setErrorCode('expired-link');
        } else if (error.code === 'auth/user-not-found') {
          setError('We could not find your account. Please make sure you are using the same email address you signed up with.');
          setErrorCode('user-not-found');
        } else if (error.code === 'auth/network-request-failed') {
          setError('Network error. Please check your internet connection and try again.');
          setErrorCode('network-error');
        } else {
          setError('Failed to verify your email. The link may have expired or is invalid.');
          setErrorCode('verification-failed');
        }
      } finally {
        setLoading(false);
        setVerifying(false);
      }
    };
    verifyUserEmail();
  }, [searchParams, verifyEmail, currentUser, refreshUser, navigate, userType]);
  // FIXED: Enhanced resend verification with better error handling
  const handleResendVerification = async () => {
    if (!currentUser || countdown > 0) return;
    try {
      console.log('Attempting to resend verification email to:', currentUser.email);
      await sendVerificationEmail();
      console.log('Verification email resent successfully');
      setCountdown(60); // Set 60 seconds cooldown
      setError('A new verification email has been sent. Please check your inbox and spam folder.');
      setErrorCode('verification-resent');
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        setError('Too many verification emails sent. Please wait a while before trying again.');
      } else if (error.message) {
        setError(`Failed to send verification email: ${error.message}`);
      } else {
        setError('Failed to send verification email. Please try again later.');
      }
      setErrorCode('resend-failed');
    }
  };
  // Handle countdown timer for resend button
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown]);
  // Helper function to determine redirect location
  function getDashboardLink() {
    if (userType === 'teacher') {
      return '/teacher-dashboard';
    } else if (userType === 'student') {
      return '/student-dashboard';
    } else {
      return '/';
    }
  }
  return <div className="min-h-screen flex flex-col bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-8413">
      <div className="flex-grow flex items-center justify-center px-4 py-12" data-id="element-8414">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 max-w-md w-full border border-white/20 shadow-xl" data-id="element-8415">
          {loading ? <div className="text-center" data-id="element-8416">
              <LoaderIcon className="h-16 w-16 text-deep-pink mx-auto mb-4 animate-spin" data-id="element-8417" />
              <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-8418">
                Verifying Your Email
              </h1>
              <p className="text-gray-300" data-id="element-8419">
                Please wait while we verify your email address...
              </p>
            </div> : success ? <div className="text-center" data-id="element-8420">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" data-id="element-8421" />
              <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-8422">
                Email Verified Successfully!
              </h1>
              <p className="text-gray-300 mb-8" data-id="element-8423">
                Your email has been verified. You will be redirected to your
                dashboard shortly.
              </p>
              <a href={getDashboardLink()} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-6 rounded-lg transition-colors inline-flex items-center" data-id="element-8424">
                <HomeIcon className="h-5 w-5 mr-2" data-id="element-8425" />
                Go to Dashboard
              </a>
            </div> : <div className="text-center" data-id="element-8426">
              <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" data-id="element-8427" />
              <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-8428">
                {errorCode === 'verification-resent' ? 'Email Sent' : 'Verification Failed'}
              </h1>
              <p className="text-gray-300 mb-6" data-id="element-8429">{error}</p>
              {/* Show different actions based on error code */}
              {(errorCode === 'expired-link' || errorCode === 'not-verified' || errorCode === 'verification-failed') && currentUser && <div className="space-y-4 mb-4" data-id="element-8430">
                    <button onClick={handleResendVerification} disabled={countdown > 0 || verifying} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-6 rounded-lg transition-colors block w-full flex items-center justify-center disabled:opacity-50" data-id="element-8431">
                      {verifying ? <LoaderIcon className="h-5 w-5 animate-spin mr-2" data-id="element-8432" /> : <MailIcon className="h-5 w-5 mr-2" data-id="element-8433" />}
                      {countdown > 0 ? `Resend Email (${countdown}s)` : 'Resend Verification Email'}
                    </button>
                  </div>}
              <div className="space-y-4" data-id="element-8434">
                {errorCode === 'verification-resent' ? <a href={currentUser ? '/teacher-email-verification' : '/teacher-login'} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-6 rounded-lg transition-colors block" data-id="element-8435">
                    Go to Verification Page
                  </a> : <a href={currentUser ? '/teacher-email-verification' : '/teacher-login'} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-6 rounded-lg transition-colors block" data-id="element-8436">
                    {currentUser ? 'Back to Verification Page' : 'Back to Login'}
                  </a>}
                <button onClick={() => window.location.reload()} className="bg-white/10 hover:bg-white/20 text-white py-2 px-6 rounded-lg transition-colors block w-full" data-id="element-8437">
                  <RefreshCwIcon className="h-4 w-4 mr-2 inline" data-id="element-8438" />
                  Try Again
                </button>
              </div>
              {/* Additional troubleshooting tips */}
              {(errorCode === 'expired-link' || errorCode === 'not-verified' || errorCode === 'verification-failed') && <div className="mt-6 bg-white/5 p-4 rounded-lg text-left" data-id="element-8439">
                  <h3 className="text-white text-sm font-medium mb-2 flex items-center" data-id="element-8440">
                    <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-8441" />
                    Troubleshooting Tips
                  </h3>
                  <ul className="text-gray-300 text-xs space-y-1 list-disc list-inside" data-id="element-8442">
                    <li data-id="element-8443">
                      Check that you're using the most recent verification email
                    </li>
                    <li data-id="element-8444">Make sure you're signed in with the correct account</li>
                    <li data-id="element-8445">
                      Try copying and pasting the verification link directly
                    </li>
                    <li data-id="element-8446">
                      Check your browser settings and try a different browser
                    </li>
                  </ul>
                </div>}
            </div>}
        </div>
      </div>
    </div>;
};